import { SendReportRequestData } from '@/types';
import { useFormik } from 'formik';
import { useTranslations } from 'next-intl';
import {
  FORM_INITIAL_VALUES,
  OTHER_OPTION_KEY,
  REPORT_OPTIONS,
  ReportObjectForm,
  validationSchema,
} from './constants';
import { FC, useEffect, useMemo } from 'react';
import { Box, Button, Dialog, DialogContent, FormGroup, Typography } from '@mui/material';
import { BtnBox, DialogHeadStyled, FormBox } from './styled';
import { COLORS } from '@/constants';
import { Checkbox, TextField } from '@/components';
import { LineWrapper } from '@/components/forms/SendRequestForm/styles';
import { useReportProperty } from '@/queris';
import { showToast } from '@/utils/showToast';

type ReportObjectModalProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
};

export const ReportObjectModal: FC<ReportObjectModalProps> = ({ isOpen, setIsOpen }) => {
  const t = useTranslations();

  const formik = useFormik<ReportObjectForm>({
    initialValues: FORM_INITIAL_VALUES,
    validationSchema: validationSchema(t),
    onSubmit: (x) => {
      const requestData = {
        codes: x.codes,
        email: x.email,
        message: x.message,
        objectLink: window.location.href,
      } as SendReportRequestData['requestBody'];

      if (x.codes && !x.codes.includes(OTHER_OPTION_KEY)) {
        delete requestData.message;
        delete requestData.email;
      }

      mutate({
        requestBody: requestData,
      });
      setIsOpen(false);
    },
  });

  const { mutate } = useReportProperty(() => {
    formik.resetForm();
    const message = t('housingCardDetails.reportObject.notifications.sendedSuccessfully');
    showToast.success(message);
  });

  const shouldShowConditionalField = useMemo(
    () => formik.values.codes.includes(OTHER_OPTION_KEY),
    [formik.values.codes],
  );

  useEffect(() => {
    if (!shouldShowConditionalField && formik.values.message) {
      void formik.setFieldValue('message', '');
    }
  }, [shouldShowConditionalField]);

  const handleClose = () => {
    setIsOpen(false);
    formik.resetForm();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogContent>
        <DialogHeadStyled>
          <Typography fontSize={24} fontWeight={600} lineHeight={1}>
            {t('housingCardDetails.reportObject.title')}
          </Typography>
          <Typography color={COLORS['gray-dark']}>
            {t('housingCardDetails.reportObject.desc')}
          </Typography>
        </DialogHeadStyled>

        <form onSubmit={formik.handleSubmit}>
          <FormBox>
            <Typography fontWeight={500}>
              {t('housingCardDetails.reportObject.question')}
            </Typography>
            <FormGroup>
              {REPORT_OPTIONS.map((option) => (
                <Checkbox
                  key={option.code}
                  name="codes"
                  checked={formik.values.codes.includes(option.code)}
                  label={t(`housingCardDetails.reportObject.options.${option.label}`)}
                  disabled={false}
                  error={formik.touched.codes && Boolean(formik.errors.codes)}
                  onChange={(_, checked) => {
                    const value = option.code;
                    if (checked) {
                      void formik.setFieldValue('codes', [...formik.values.codes, value]);
                    } else {
                      void formik.setFieldValue(
                        'codes',
                        formik.values.codes.filter((item) => item !== value),
                      );
                    }
                  }}
                />
              ))}
            </FormGroup>
            {formik.touched.codes && formik.errors.codes && (
              <Typography color="red">{formik.errors.codes}</Typography>
            )}
          </FormBox>
          <FormBox>
            {shouldShowConditionalField && (
              <LineWrapper>
                <TextField
                  label={t('housingCardDetails.reportObject.message')}
                  variant="outlined"
                  multiline
                  minRows={3}
                  size="small"
                  name="message"
                  width={'100%'}
                  fullWidth
                  onChange={formik.handleChange}
                  value={formik.values.message}
                  error={!!formik.errors.message && formik.touched.message}
                  helperText={formik.touched.message ? formik.errors.message || '' : ''}
                />
              </LineWrapper>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Typography fontSize={16} lineHeight={1} color={COLORS['gray-dark']}>
                {t('housingCardDetails.reportObject.emailMessage')}
              </Typography>
              <LineWrapper>
                <TextField
                  label={t('housingCardDetails.reportObject.email')}
                  variant="outlined"
                  size="small"
                  name="email"
                  width={'100%'}
                  fullWidth
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  error={!!formik.errors.email && formik.touched.email}
                  helperText={formik.touched.email ? formik.errors.email || '' : ''}
                  disabled={formik.values.emailController}
                />
              </LineWrapper>
            </Box>
            <LineWrapper>
              <Checkbox
                label={t('housingCardDetails.reportObject.emailController')}
                checked={formik.values.emailController}
                onChange={(e) => {
                  if ((e.target as HTMLInputElement).checked) {
                    void formik.setFieldValue('email', '');
                  }
                  void formik.setFieldValue(
                    'emailController',
                    (e.target as HTMLInputElement).checked,
                  );
                }}
                name="emailController"
              />
            </LineWrapper>
          </FormBox>

          <BtnBox>
            <Button
              size="medium"
              onClick={handleClose}
              variant="primary"
              sx={{
                height: '44px',
              }}
            >
              {t('housingCardDetails.reportObject.buttons.cancel')}
            </Button>
            <Button
              variant="secondary"
              type="submit"
              sx={{
                height: '44px',
              }}
            >
              {t('housingCardDetails.reportObject.buttons.send')}
            </Button>
          </BtnBox>
        </form>
      </DialogContent>
    </Dialog>
  );
};
